import { ReactNode, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Skeleton } from "../../../components/Skeleton";
import { useSignedUrlQuery } from "../../../services/useSignedUrl";

/** Split a bucket-root S3 key (e.g. `badge-center/tiers/recruit.svg`) for useSignedUrl. */
function splitS3Key(key: string): { path: string; s3Key: string } {
    const slash = key.lastIndexOf("/");
    if (slash === -1) return { path: "app/assets/images", s3Key: key };
    return { path: key.slice(0, slash), s3Key: key.slice(slash + 1) };
}

interface SignedImageProps {
    /** Bucket-root S3 key of the image (resolved to a signed URL). */
    imageKey?: string;
    alt?: string;
    /** Base size classes applied to the skeleton, image, and fallback. */
    sizeClassName: string;
    /** Extra classes (e.g. rounding) applied to the loading skeleton only. */
    skeletonClassName?: string;
    /**
     * Rendered when there is no source key or the image can't be resolved/loaded.
     * Receives the merged size classes so the fallback matches the image footprint.
     */
    fallback: (sizeClasses: string) => ReactNode;
    className?: string;
}

/**
 * Renders an S3-backed image resolved to a signed URL, with a loading skeleton,
 * stale-load guarding, and error fallback. Shared by BadgeImage and TierImage so
 * the loading/error behaviour stays in one place.
 */
export function SignedImage({ imageKey, alt = "", sizeClassName, skeletonClassName, fallback, className }: SignedImageProps) {
    const { path, s3Key } = imageKey ? splitS3Key(imageKey) : { path: undefined, s3Key: "" };
    const { data: url = "", isError } = useSignedUrlQuery(s3Key, undefined, path);
    const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
    const latestUrlRef = useRef(url);
    latestUrlRef.current = url;
    const [erroredUrl, setErroredUrl] = useState<string | null>(null);

    const sizeClasses = twMerge(sizeClassName, className);

    // Fall back when there is no source key, the signed-URL request failed
    // without ever resolving a URL, or the resolved image failed to load.
    // Without the !s3Key and isError guards an unresolved URL leaves the
    // Skeleton rendered with no <img> to ever fire onLoad, hanging forever. We
    // only fall back on isError when there is no url: React Query keeps the last
    // successful data when a background refetch fails, so a transient assets
    // error shouldn't replace a still-valid image.
    if (!imageKey || !s3Key || (isError && !url) || erroredUrl === url) {
        return <>{fallback(sizeClasses)}</>;
    }

    const loaded = !!url && loadedUrl === url;

    return (
        <>
            {!loaded && <Skeleton className={twMerge(sizeClasses, skeletonClassName)} />}
            {url && (
                <img
                    key={url}
                    src={url}
                    alt={alt}
                    onLoad={() => {
                        // Ignore stale load events from a previous img: when imageKey
                        // changes quickly, an older img's onLoad can fire after the
                        // current url has loaded and would otherwise revert `loaded`.
                        if (latestUrlRef.current === url) setLoadedUrl(url);
                    }}
                    onError={() => setErroredUrl(url)}
                    className={twMerge(sizeClasses, "object-contain", !loaded && "hidden")}
                />
            )}
        </>
    );
}
